/**
 * Store for the Cloudflare Turnstile token.
 *
 * Tokens are single-use — Cloudflare invalidates them after the first
 * siteverify call. After each request that consumes a token, call
 * clearToken() + triggerReset() so the widget fetches a fresh one.
 */

let _token: string | null = null;
let _waiters: ((token: string) => void)[] = [];
let _resetCallback: (() => void) | null = null;

export const setToken = (token: string): void => {
    const waiter = _waiters.shift();
    if (waiter) {
        waiter(token);
        if (_waiters.length > 0) _resetCallback?.();
        return;
    }

    _token = token;
    _tokenIssuedAt = Date.now();
};

export const clearToken = (): void => {
    _token = null;
};

export const registerResetCallback = (cb: () => void): void => {
    _resetCallback = cb;
};

export const triggerReset = (): void => {
    clearToken();
    _resetCallback?.();
};

/** Resolves immediately if a token is available, otherwise waits for the next one. */
export const getToken = (): Promise<string> => {
    if (_token) {
        const token = _token;
        clearToken();
        return Promise.resolve(token);
    }

    return new Promise(resolve => {
        _waiters.push(resolve);
        _resetCallback?.();
    });
};
